import { List } from 'components/List';
import { Alert } from 'domain/models';
import { isOdd } from 'domain/utils';
import { dateTimeFormat } from 'domain/utils/date.utils';
import { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const INIT_MAX_LIST = 5;

export const AlertList = ({
	list,
	isFull,
}: {
	list: Alert[];
	isFull?: boolean;
}) => {
	const { t } = useTranslation('labels');

	const [viewAll, setViewAll] = useState(false);

	const sliced = useMemo(() => {
		if (viewAll) {
			return list;
		}

		return (list || []).slice(0, INIT_MAX_LIST);
	}, [list, viewAll]);

	const showViewAll = useMemo(() => {
		if (viewAll) {
			return false;
		}
		return list.length > INIT_MAX_LIST;
	}, [list]);

	isFull = isFull || false;
	if (!list) {
		return null;
	}

	const size = isFull ? '3' : '4';
	return (
		<div className='w-full'>
			<List size='sm'>
				<List.RowGroup className='border-b'>
					<List.Row>
						{isFull !== false && (
							<List.Header className={`w-${size[0]}/12`}>
								{t('labels:fired_at')}
							</List.Header>
						)}
						<List.Header className={`w-${size[1]}/12`}>
							{t('labels:date')}
						</List.Header>
						<List.Header className={`w-${size[2]}/12`}>
							{t('labels:duration')}
						</List.Header>
						<List.Header className={`w-${size[3]}/12`}>
							{t('labels:alert')}
						</List.Header>
					</List.Row>
				</List.RowGroup>
				<List.RowGroup className='scroll h-32 max-h-32 overflow-scroll'>
					{list.length > 0 &&
						sliced.map((alert, index) => {
							return (
								<List.Row
									key={`${index}-${alert?.firedAt}`}
									className={!isOdd(index) ? 'border-none bg-gray-100' : ''}>
									{isFull !== false && (
										<List.Cell className={`w-${size[0]}/12`}>
											{dateTimeFormat(alert?.firedAt)} -
										</List.Cell>
									)}
									<List.Cell className={`w-${size[1]}/12`}>
										{dateTimeFormat(alert?.alertTime)} &rarr;{' '}
										{dateTimeFormat(alert?.endTime)}
									</List.Cell>
									<List.Cell className={`w-${size[2]}/12`}>
										<Suspense fallback={<div>...</div>}>
											<AlertDuration values={alert} />
										</Suspense>
									</List.Cell>
									<List.Cell className={`w-${size[3]}/12`}>
										{alert?.alertDefinition?.label}
									</List.Cell>
								</List.Row>
							);
						})}
					{list.length === 0 && (
						<List.Row className={'border-none bg-gray-100'}>
							<List.Cell className={'text-neutral-500'}>
								{t('labels:no_alerts')}
							</List.Cell>
						</List.Row>
					)}
					{showViewAll && (
						<List.Row
							className={'flex justify-center'}
							onClick={() => setViewAll(true)}>
							<List.Cell className={'w-fit text-blue-500 hover:underline'}>
								{t('labels:view_all')}
							</List.Cell>
						</List.Row>
					)}
				</List.RowGroup>
			</List>
		</div>
	);
};

const AlertDuration = ({ values }: { values: Alert }) => {
	// Calculate the duration, if it's still active, we show the current time
	const endTime = values?.endTime ? new Date(values?.endTime) : new Date();
	const alertTime = values?.alertTime
		? new Date(values?.alertTime)
		: new Date();

	const diff = endTime.getTime() - alertTime.getTime();

	const duration = diff / 1000;

	// Formatting
	const days = Math.floor(duration / 86400);
	const hours = Math.floor((duration % 86400) / 3600);

	const pad2 = (n: number) => {
		return n < 10 ? '0' + n : n;
	};

	return (
		<span>
			{days}d {pad2(hours)}h
		</span>
	);
};
